/**
 * Talent trees (PLAN §11 "Talents"): 18 nodes per class over 3 tiers, with 3 mutually
 * exclusive tier-3 capstones each. Plus the pick orders behind the recommended (bot) build and
 * the two named presets per class.
 *
 * A pick order is a priority list, not a loadout: `talents.fillLoadout` takes nodes from it while
 * the result stays valid, so the same order yields a sensible build at any hero level and any
 * rank tier (capstones are skipped until they unlock).
 */
import { CLASS } from '@keystone/content';
import type { ClassId } from '@keystone/sim';
import type { TalentId, TalentNode } from '../../types';
import { BLADEMASTER_TREE } from './blademaster';
import { LIGHTBINDER_TREE } from './lightbinder';
import { STORMCALLER_TREE } from './stormcaller';
import { WARDEN_TREE } from './warden';

export { BLADEMASTER_CAPSTONE, BLADEMASTER_TREE } from './blademaster';
export { LIGHTBINDER_CAPSTONE, LIGHTBINDER_TREE } from './lightbinder';
export { STORMCALLER_CAPSTONE, STORMCALLER_TREE } from './stormcaller';
export { WARDEN_CAPSTONE, WARDEN_TREE } from './warden';

export const TALENT_TREES: Readonly<Record<ClassId, readonly TalentNode[]>> = {
  [CLASS.warden]: WARDEN_TREE,
  [CLASS.lightbinder]: LIGHTBINDER_TREE,
  [CLASS.blademaster]: BLADEMASTER_TREE,
  [CLASS.stormcaller]: STORMCALLER_TREE,
};

export type TalentPresetId = 'trash' | 'boss';

export interface TalentPresetDef {
  id: TalentPresetId;
  name: string;
  description: string;
  /** Priority order; trimmed to the points and tiers available. */
  order: readonly TalentId[];
}

/** Recommended build for bots (and the default for a new character), in pick order. */
export const RECOMMENDED_ORDER: Readonly<Record<ClassId, readonly TalentId[]>> = {
  [CLASS.warden]: [
    // The first three must fit in 5 points (progression.test pins this).
    'warden.t1.slam_interrupt',
    'warden.t1.bash_charges',
    'warden.t1.wide_ground',
    'warden.t1.resounding_slam',
    'warden.t1.ricochet',
    'warden.t1.sweeping_guard',
    'warden.t2.sanctified_ground',
    'warden.t2.bulwark_ward',
    'warden.t2.shield_mastery',
    'warden.t2.retaliation',
    'warden.t3.twin_slams',
    'warden.t3.last_stand_heal',
    'warden.t3.counterweight',
    'warden.t2.iron_chain',
    'warden.t2.warding_bash',
    'warden.t1.challenging_roar',
  ],
  [CLASS.lightbinder]: [
    'lightbinder.t1.flash_hot',
    'lightbinder.t1.reverberation',
    'lightbinder.t1.judgment_of_light',
    'lightbinder.t1.steadfast_aegis',
    'lightbinder.t2.inflorescence',
    'lightbinder.t2.divine_revelation',
    'lightbinder.t2.aegis_of_light',
    'lightbinder.t3.glimmer',
    'lightbinder.t3.rising_sunlight',
    'lightbinder.t3.last_rites',
    'lightbinder.t2.word_of_radiance',
    'lightbinder.t2.tidal_radiance',
    'lightbinder.t3.purifying_wave',
    'lightbinder.t1.swift_verdict',
  ],
  [CLASS.blademaster]: [
    'blademaster.t1.sweeping_strikes',
    'blademaster.t1.execute_range',
    'blademaster.t1.tactical_reserve',
    'blademaster.t2.fervor_of_battle',
    'blademaster.t2.test_of_might',
    'blademaster.t2.battlelord',
    'blademaster.t1.disrupting_pummel',
    'blademaster.t1.bloodcraze',
    'blademaster.t2.second_wind',
    'blademaster.t3.executioners_precision',
    'blademaster.t3.executioner',
    'blademaster.t3.warbreaker',
    'blademaster.t2.unhinged',
    'blademaster.t2.impending_victory',
    'blademaster.t1.shockwave',
  ],
  [CLASS.stormcaller]: [
    // Rotation rework (docs/design/classes/stormcaller.md §5): dungeon-generic, Storm Unleashed capstone.
    'stormcaller.t1.forked',
    'stormcaller.t1.lava_flow',
    'stormcaller.t1.thundering_shear',
    'stormcaller.t1.spreading_flame',
    'stormcaller.t1.aftershock',
    'stormcaller.t2.surge_of_power',
    'stormcaller.t2.static_discharge',
    'stormcaller.t2.capacitor',
    'stormcaller.t2.earthshaker',
    'stormcaller.t3.thundercrash',
    'stormcaller.t1.spirit_walk',
    'stormcaller.t3.storm_unleashed',
    'stormcaller.t3.echo_of_elements',
    'stormcaller.t2.ancestral_guidance',
  ],
};

export const TALENT_PRESETS: Readonly<Record<ClassId, readonly TalentPresetDef[]>> = {
  [CLASS.warden]: [
    {
      id: 'trash',
      name: 'Trash',
      description: 'Wide Cleave Strike and Consecrated Ground, ricocheting Aegis, AoE taunt and the Avenger Recall engine for big pulls.',
      order: [
        'warden.t1.sweeping_guard',
        'warden.t1.wide_ground',
        'warden.t1.challenging_roar',
        'warden.t1.slam_interrupt',
        'warden.t1.ricochet',
        'warden.t2.shield_mastery',
        'warden.t2.sanctified_ground',
        'warden.t2.retaliation',
        'warden.t2.bulwark_ward',
        'warden.t3.counterweight',
        'warden.t3.avenger',
        'warden.t3.twin_slams',
        'warden.t2.warding_bash',
        'warden.t1.resounding_slam',
        'warden.t1.bash_charges',
      ],
    },
    {
      id: 'boss',
      name: 'Boss',
      description: 'Single-target Resolve, Opening from Shield Wall Bash, Counterweight Bastion and the Unbroken Last Stand for tank-busters.',
      order: [
        'warden.t1.resounding_slam',
        'warden.t1.bash_charges',
        'warden.t1.slam_interrupt',
        'warden.t1.wide_ground',
        'warden.t1.ricochet',
        'warden.t2.bulwark_ward',
        'warden.t2.sanctified_ground',
        'warden.t2.warding_bash',
        'warden.t2.retaliation',
        'warden.t2.shield_mastery',
        'warden.t3.twin_slams',
        'warden.t3.last_stand_heal',
        'warden.t3.counterweight',
        'warden.t2.iron_chain',
        'warden.t1.sweeping_guard',
      ],
    },
  ],
  [CLASS.lightbinder]: [
    {
      id: 'trash',
      name: 'Trash',
      description: 'Avenging Crusader turns Flash Heal into Hammer of Dawn in the window; smart heals on Judgment and Word of Light, faster Radiant Wave.',
      order: [
        'lightbinder.t1.judgment_of_light',
        'lightbinder.t1.flash_hot',
        'lightbinder.t1.reverberation',
        'lightbinder.t1.steadfast_aegis',
        'lightbinder.t1.swift_verdict',
        'lightbinder.t2.word_of_radiance',
        'lightbinder.t2.inflorescence',
        'lightbinder.t2.divine_revelation',
        'lightbinder.t2.tidal_radiance',
        'lightbinder.t3.glimmer',
        'lightbinder.t3.rising_sunlight',
        'lightbinder.t3.crusader',
        'lightbinder.t3.purifying_wave',
      ],
    },
    {
      id: 'boss',
      name: 'Boss',
      description: "Three Holy Shock charges, Sunlit Shock in the window, a stronger Guardian's Aegis and Last Rites for tank damage.",
      order: [
        'lightbinder.t1.flash_hot',
        'lightbinder.t1.reverberation',
        'lightbinder.t1.judgment_of_light',
        'lightbinder.t1.steadfast_aegis',
        'lightbinder.t2.inflorescence',
        'lightbinder.t2.divine_revelation',
        'lightbinder.t2.aegis_of_light',
        'lightbinder.t3.glimmer',
        'lightbinder.t3.rising_sunlight',
        'lightbinder.t3.last_rites',
        'lightbinder.t1.mobile_flash',
        'lightbinder.t2.unshackled',
        'lightbinder.t3.purifying_wave',
      ],
    },
  ],
  [CLASS.blademaster]: [
    {
      id: 'trash',
      name: 'Trash',
      description: "Eight-target Cleave into Shockwave, self-cleaving Mortal Strike, Warbreaker and the Ravager's Reach Bladestorm.",
      order: [
        'blademaster.t1.sweeping_strikes',
        'blademaster.t1.shockwave',
        'blademaster.t1.execute_range',
        'blademaster.t1.disrupting_pummel',
        'blademaster.t1.bloodcraze',
        'blademaster.t2.fervor_of_battle',
        'blademaster.t2.unhinged',
        'blademaster.t2.second_wind',
        'blademaster.t2.test_of_might',
        'blademaster.t3.warbreaker',
        'blademaster.t3.ravagers_reach',
        'blademaster.t2.battlelord',
        'blademaster.t1.tactical_reserve',
      ],
    },
    {
      id: 'boss',
      name: 'Boss',
      description: "Three Overpower charges, Colossus Smash to 5 Wounds, Battlelord resets, Executioner's Precision and the Deep Wounds capstone.",
      order: [
        'blademaster.t1.execute_range',
        'blademaster.t1.tactical_reserve',
        'blademaster.t1.bloodcraze',
        'blademaster.t1.disrupting_pummel',
        'blademaster.t1.sweeping_strikes',
        'blademaster.t2.test_of_might',
        'blademaster.t2.battlelord',
        'blademaster.t2.impending_victory',
        'blademaster.t2.second_wind',
        'blademaster.t3.executioners_precision',
        'blademaster.t3.warbreaker',
        'blademaster.t3.executioner',
        'blademaster.t2.unhinged',
        'blademaster.t1.shockwave',
      ],
    },
  ],
  [CLASS.stormcaller]: [
    {
      id: 'trash',
      name: 'Trash',
      description: 'Forked Chain Lightning and Tempest, spreading Flame Shock, Aftershock refunds, the Thundercrash transform and Storm Unleashed Earthquake.',
      order: [
        'stormcaller.t1.forked',
        'stormcaller.t1.spreading_flame',
        'stormcaller.t1.aftershock',
        'stormcaller.t1.thundering_shear',
        'stormcaller.t1.lava_flow',
        'stormcaller.t2.surge_of_power',
        'stormcaller.t2.capacitor',
        'stormcaller.t2.earthshaker',
        'stormcaller.t3.thundercrash',
        'stormcaller.t1.spirit_walk',
        'stormcaller.t3.storm_unleashed',
        'stormcaller.t2.static_discharge',
        'stormcaller.t2.ancestral_guidance',
        'stormcaller.t3.echo_of_elements',
      ],
    },
    {
      id: 'boss',
      name: 'Boss',
      description: 'Casting on the move, banked Lava Surges, Static Discharge, Thundercrash and the Master of Flame capstone.',
      order: [
        'stormcaller.t1.lava_flow',
        'stormcaller.t1.walking_bolt',
        'stormcaller.t1.thundering_shear',
        'stormcaller.t1.spirit_walk',
        'stormcaller.t2.static_discharge',
        'stormcaller.t2.volcanic_stride',
        'stormcaller.t2.surge_of_power',
        'stormcaller.t2.ancestral_guidance',
        'stormcaller.t3.echo_of_elements',
        'stormcaller.t3.thundercrash',
        'stormcaller.t1.aftershock',
        'stormcaller.t3.master_of_flame',
        'stormcaller.t2.capacitor',
        'stormcaller.t1.forked',
      ],
    },
  ],
};
